import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, CheckCircle2, Circle, ChevronDown, ChevronUp } from 'lucide-react';

interface ChecklistItem {
  id: string;
  category: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  completed: boolean;
}

const CHECKLIST_ITEMS: ChecklistItem[] = [
  { id: '1', category: 'Dados da Empresa', title: 'Contrato Social (cópia autenticada)', description: 'Documento fundador da empresa', priority: 'high', completed: false },
  { id: '2', category: 'Dados da Empresa', title: 'Alteração Contratual (se houver)', description: 'Últimas alterações do contrato', priority: 'medium', completed: false },
  { id: '3', category: 'Dados da Empresa', title: 'Cartão CNPJ (cópia)', description: 'Comprovante de registro na Receita Federal', priority: 'high', completed: false },
  { id: '4', category: 'Dados da Empresa', title: 'Comprovante de Endereço', description: 'Conta de água, luz ou telefone recente', priority: 'high', completed: false },
  { id: '5', category: 'Dados da Empresa', title: 'Certidão de Regularidade (CNPJ, INSS, FGTS)', description: 'Comprovação de regularidade fiscal', priority: 'high', completed: false },
  { id: '6', category: 'Dados da Empresa', title: 'Certidão Negativa de Débitos (CND)', description: 'Comprovação de ausência de débitos com BNDES', priority: 'high', completed: false },
  { id: '7', category: 'Dados dos Sócios', title: 'RG e CPF de cada sócio (cópia)', description: 'Documentos de identidade', priority: 'high', completed: false },
  { id: '8', category: 'Dados dos Sócios', title: 'Comprovante de Endereço de cada sócio', description: 'Endereço residencial atual', priority: 'high', completed: false },
  { id: '9', category: 'Dados dos Sócios', title: 'Declaração de Imposto de Renda (últimos 2 anos)', description: 'Comprovação de renda pessoal', priority: 'high', completed: false },
  { id: '10', category: 'Dados dos Sócios', title: 'Aval Pessoal de cada sócio', description: 'Documento de garantia pessoal', priority: 'high', completed: false },
  { id: '11', category: 'Documentação Financeira', title: 'Balanço Patrimonial (últimos 2 anos)', description: 'Demonstração da situação financeira', priority: 'high', completed: false },
  { id: '12', category: 'Documentação Financeira', title: 'DRE — Demonstração de Resultado (últimos 2 anos)', description: 'Demonstração de lucro/prejuízo', priority: 'high', completed: false },
  { id: '13', category: 'Documentação Financeira', title: 'Demonstração de Fluxo de Caixa (últimos 12 meses)', description: 'Movimentação de caixa', priority: 'high', completed: false },
  { id: '14', category: 'Documentação Financeira', title: 'Fluxo de Caixa Projetado (próximos 3 anos)', description: 'Previsão de caixa com a nova máquina', priority: 'high', completed: false },
  { id: '15', category: 'Documentação Financeira', title: 'Relatório de Contas a Receber', description: 'Clientes e valores a receber', priority: 'medium', completed: false },
  { id: '16', category: 'Documentação Financeira', title: 'Relatório de Contas a Pagar', description: 'Fornecedores e valores a pagar', priority: 'medium', completed: false },
  { id: '17', category: 'Documentação da Máquina', title: 'Contrato de Compra da Máquina (assinado)', description: 'Acordo com o fornecedor', priority: 'high', completed: false },
  { id: '18', category: 'Documentação da Máquina', title: 'Nota Fiscal Pro Forma (do fabricante)', description: 'Cotação oficial do fabricante', priority: 'high', completed: false },
  { id: '19', category: 'Documentação da Máquina', title: 'Especificação Técnica da Máquina', description: 'Dados técnicos e modelo', priority: 'high', completed: false },
  { id: '20', category: 'Documentação da Máquina', title: 'Comprovante de Cadastro no CFI (BNDES)', description: 'Máquina cadastrada no CFI do BNDES', priority: 'high', completed: false },
  { id: '21', category: 'Documentação de Garantia', title: 'Aval dos Sócios (assinado)', description: 'Garantia pessoal dos sócios', priority: 'high', completed: false },
  { id: '22', category: 'Documentação de Garantia', title: 'Matrícula do Imóvel (se hipoteca)', description: 'Comprovante de propriedade', priority: 'medium', completed: false },
  { id: '23', category: 'Documentação de Garantia', title: 'Certidão de Ônus (se hipoteca)', description: 'Comprovação de ausência de ônus', priority: 'medium', completed: false },
];

const PRIORITY_CONFIG = {
  high: { label: 'Essencial', color: '#dc2626', bg: '#fef2f2' },
  medium: { label: 'Importante', color: '#d97706', bg: '#fffbeb' },
  low: { label: 'Opcional', color: '#059669', bg: '#f0fdf4' },
};

const CATEGORY_COLORS: Record<string, string> = {
  'Dados da Empresa': '#2563eb',
  'Dados dos Sócios': '#7c3aed',
  'Documentação Financeira': '#059669',
  'Documentação da Máquina': '#d97706',
  'Documentação de Garantia': '#dc2626',
};

export default function Checklist() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ChecklistItem[]>(CHECKLIST_ITEMS);
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Dados da Empresa');

  const handleToggle = (id: string) => {
    setItems(prev => prev.map(item => item.id === id ? { ...item, completed: !item.completed } : item));
  };

  const categories = Array.from(new Set(items.map(item => item.category)));
  const completedCount = items.filter(item => item.completed).length;
  const progressPct = Math.round((completedCount / items.length) * 100);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <header style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px', height: 60, display: 'flex', alignItems: 'center', gap: 16 }}>
          <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 12px', fontSize: 13, color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ArrowLeft size={14} /> Voltar
          </button>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>Checklist de Documentação</h1>
            <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>BNDES Automático — 23 documentos</p>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '28px 24px' }}>

        {/* Progresso */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 20, padding: '24px 28px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: 0 }}>Progresso</h2>
            <span style={{ fontSize: 28, fontWeight: 800, color: '#059669', letterSpacing: '-1px' }}>{completedCount}/{items.length}</span>
          </div>
          <div style={{ background: '#f3f4f6', borderRadius: 999, height: 10, overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(90deg, #059669, #0891b2)', height: '100%', borderRadius: 999, width: `${progressPct}%`, transition: 'width 0.4s ease' }} />
          </div>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '8px 0 0' }}>{progressPct}% completo</p>
        </div>

        {/* Categorias */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {categories.map(category => {
            const catItems = items.filter(item => item.category === category);
            const catCompleted = catItems.filter(item => item.completed).length;
            const isExpanded = expandedCategory === category;
            const catColor = CATEGORY_COLORS[category] || '#059669';

            return (
              <div key={category} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 18, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <button onClick={() => setExpandedCategory(isExpanded ? null : category)}
                  style={{ width: '100%', padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: catColor, flexShrink: 0 }} />
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>{category}</h3>
                      <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{catCompleted} de {catItems.length} completo</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ background: '#f3f4f6', borderRadius: 999, width: 80, height: 6 }}>
                      <div style={{ background: catColor, height: '100%', borderRadius: 999, width: `${Math.round(catCompleted / catItems.length * 100)}%`, transition: 'width 0.3s' }} />
                    </div>
                    {isExpanded ? <ChevronUp size={18} color="#9ca3af" /> : <ChevronDown size={18} color="#9ca3af" />}
                  </div>
                </button>

                {isExpanded && (
                  <div style={{ borderTop: '1px solid #f3f4f6', padding: '8px 14px 14px' }}>
                    {catItems.map(item => {
                      const pConfig = PRIORITY_CONFIG[item.priority];
                      return (
                        <button key={item.id} onClick={() => handleToggle(item.id)}
                          style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 14, padding: '12px', borderRadius: 12, background: item.completed ? '#f8fafc' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left', marginBottom: 4, transition: 'background 0.15s' }}>
                          <div style={{ marginTop: 2, flexShrink: 0 }}>
                            {item.completed
                              ? <CheckCircle2 size={20} color="#059669" />
                              : <Circle size={20} color="#d1d5db" />}
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ fontSize: 14, fontWeight: 600, color: item.completed ? '#9ca3af' : '#111827', margin: '0 0 3px', textDecoration: item.completed ? 'line-through' : 'none' }}>
                              {item.title}
                            </p>
                            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>{item.description}</p>
                          </div>
                          <span style={{ background: pConfig.bg, color: pConfig.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, flexShrink: 0, marginTop: 2 }}>
                            {pConfig.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Exportar */}
        <button onClick={() => alert('Exportação PDF em desenvolvimento')}
          style={{ width: '100%', background: 'linear-gradient(135deg, #059669, #0891b2)', color: '#fff', border: 'none', borderRadius: 14, padding: '16px', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 20 }}>
          <Download size={18} /> Exportar Checklist em PDF
        </button>

        {/* Dicas */}
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 18, padding: '20px 24px', marginTop: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#1e3a8a', margin: '0 0 12px' }}>💡 Dicas Importantes</h3>
          {['Organize os documentos em pastas por categoria', 'Todos os documentos essenciais devem ser originais ou autenticados', 'Leve cópias extras ao banco', 'Consulte o gerente do banco sobre dúvidas específicas', 'Use este checklist como guia antes de ir ao banco'].map((tip, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, fontSize: 14, color: '#1d4ed8', marginBottom: 6 }}>
              <span>•</span> {tip}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}